import gql from 'graphql-tag';

import { executeGqlMutation, executeGqlQuery } from 'src/graphql/client';
import { FRAGMENT_JOB_ID } from 'src/graphql/job';
import { waitForJob } from 'src/graphql/job/waitForJob';
import { getSingleProp } from 'src/lib/graphql';

const SNAPSHOT_QUERY = gql`
  query UsageReportDashboard($customerId: ID!) {
    usageReportDashboard(customerId: $customerId) {
      general {
        metric
        definition
        count
      }
      allocatedLicenses {
        email
        lastActive
        lastSignedIn
      }
      superUsers30d {
        email
        lastActive
        lastSignedIn
      }
      activeUsers30d {
        email
        lastActive
        lastSignedIn
      }
      quarterlyActiveUsers90d {
        email
        lastActive
        lastSignedIn
      }
      inactiveUsers90d {
        email
        lastActive
        lastSignedIn
      }
      workspaces90d {
        name
        lastActive
      }
      weeklyTrends {
        week
        superUsersCount
        activeUsersCount
        inactiveUsersCount
        adoptionRatio
        activeWorkspacesCount
        savedTemplateGenerationsCount
      }
    }
  }
`;

const TRENDS_QUERY = gql`
  query UsageReportWeeklyTrends($customerId: ID!, $startDate: String, $endDate: String) {
    usageReportWeeklyTrends(customerId: $customerId, startDate: $startDate, endDate: $endDate) {
      week
      superUsersCount
      activeUsersCount
      inactiveUsersCount
      adoptionRatio
      activeWorkspacesCount
      savedTemplateGenerationsCount
    }
  }
`;

export interface UsageReportMetric {
  metric: string;
  definition: string;
  count: number;
}

export interface UsageReportUser {
  email: string;
  lastActive: string;
  lastSignedIn: string;
}

export interface UsageReportWorkspace {
  name: string;
  lastActive: string;
}

export interface UsageReportWeeklyTrend {
  week: string;
  superUsersCount: number;
  activeUsersCount: number;
  inactiveUsersCount: number;
  adoptionRatio: number;
  activeWorkspacesCount: number;
  savedTemplateGenerationsCount: number;
}

export interface UsageReportSnapshot {
  general: UsageReportMetric[];
  allocatedLicenses: UsageReportUser[];
  superUsers30d: UsageReportUser[];
  activeUsers30d: UsageReportUser[];
  quarterlyActiveUsers90d: UsageReportUser[];
  inactiveUsers90d: UsageReportUser[];
  workspaces90d: UsageReportWorkspace[];
  weeklyTrends: UsageReportWeeklyTrend[];
}

export interface UsageReportDashboardData extends UsageReportSnapshot {
  weeklyTrends: UsageReportWeeklyTrend[];
}

const REQUEST_DASHBOARD_MUTATION = gql`
  mutation RequestUsageReportDashboard(
    $customerId: ID!
    $startDate: String
    $endDate: String
    $trendsOnly: Boolean
  ) {
    requestUsageReportDashboard(
      customerId: $customerId
      startDate: $startDate
      endDate: $endDate
      trendsOnly: $trendsOnly
    ) {
      ...JobId
    }
  }
  ${FRAGMENT_JOB_ID}
`;

export const queryUsageReportDashboard = async (
  customerId: string
): Promise<UsageReportSnapshot> => {
  const result = await executeGqlQuery({
    query: SNAPSHOT_QUERY,
    variables: { customerId },
  });
  const data = getSingleProp<UsageReportSnapshot>(result.data);
  if (!data) throw new Error('UsageReportDashboard query returned empty data');
  return data;
};

export const queryUsageReportWeeklyTrends = async (
  customerId: string,
  startDate?: string,
  endDate?: string
): Promise<UsageReportWeeklyTrend[]> => {
  const variables: { customerId: string; startDate?: string; endDate?: string } = { customerId };
  if (startDate) variables.startDate = startDate;
  if (endDate) variables.endDate = endDate;

  const result = await executeGqlQuery({
    query: TRENDS_QUERY,
    variables,
  });
  const data = getSingleProp<UsageReportWeeklyTrend[]>(result.data);
  if (!data) throw new Error('UsageReportWeeklyTrends query returned empty data');
  return data;
};

const requestDashboardJob = async (
  customerId: string,
  options?: { startDate?: string; endDate?: string; trendsOnly?: boolean }
): Promise<string> => {
  const variables: Record<string, unknown> = { customerId };
  if (options?.startDate) variables.startDate = options.startDate;
  if (options?.endDate) variables.endDate = options.endDate;
  if (options?.trendsOnly) variables.trendsOnly = options.trendsOnly;

  const response = await executeGqlMutation({
    mutation: REQUEST_DASHBOARD_MUTATION,
    variables,
  });
  const job = getSingleProp<{ id: string }>(response.data);
  if (!job) throw new Error('RequestUsageReportDashboard mutation returned empty data');
  return job.id;
};

export const requestAndAwaitDashboard = async (
  customerId: string
): Promise<UsageReportSnapshot> => {
  const jobId = await requestDashboardJob(customerId);
  await waitForJob(jobId);
  return queryUsageReportDashboard(customerId);
};

export const requestAndAwaitWeeklyTrends = async (
  customerId: string,
  startDate?: string,
  endDate?: string
): Promise<UsageReportWeeklyTrend[]> => {
  const jobId = await requestDashboardJob(customerId, {
    startDate,
    endDate,
    trendsOnly: true,
  });
  await waitForJob(jobId);
  return queryUsageReportWeeklyTrends(customerId, startDate, endDate);
};
